import { Tooltip } from '@/ui'
import { StickyNoteIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

const CONFETTI_COLORS = ['#a855f7', '#ec4899', '#f59e0b', '#22c55e', '#3b82f6', '#ef4444']
const PARTICLE_COUNT = 10

function getTooltip(count: number): string {
	if (count >= 20) { return `${count} notes — someone write a biography already` }
	if (count >= 10) { return `${count} notes — practically a novel at this point` }
	if (count >= 5) { return `${count} notes — this one's a bestseller` }
	return `${count} internal note${count > 1 ? 's' : ''}`
}

function ConfettiParticles() {
	return (
		<>
			<style>
				{`
				@keyframes confetti-burst {
					0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
					100% { transform: translate(calc(-50% + var(--cb-dx)), calc(-50% + var(--cb-dy))) scale(0); opacity: 0; }
				}
			`}
			</style>
			<span className="absolute inset-0 pointer-events-none overflow-visible" aria-hidden="true">
				{Array.from({ length: PARTICLE_COUNT }, (_, i) => {
					const angle = (i / PARTICLE_COUNT) * Math.PI * 2
					const distance = 20 + (i * 7) % 12
					return (
						<span
							key={i}
							className="absolute left-1/2 top-1/2 size-1.5 rounded-full"
							style={{
								backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
								'--cb-dx': `${Math.cos(angle) * distance}px`,
								'--cb-dy': `${Math.sin(angle) * distance}px`,
								animation: `confetti-burst 600ms ease-out ${i * 20}ms forwards`,
							} as React.CSSProperties}
						/>
					)
				})}
			</span>
		</>
	)
}

export function NotesBadge({ count, shrink }: { count: number; shrink?: boolean }) {
	const [showConfetti, setShowConfetti] = useState(false)
	const clickCount = useRef(0)
	const clickTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
	const confettiTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

	useEffect(() => {
		return () => {
			clearTimeout(clickTimer.current)
			clearTimeout(confettiTimer.current)
		}
	}, [])

	const handleClick = useCallback((e: React.MouseEvent) => {
		e.stopPropagation()
		e.preventDefault()
		clickCount.current++

		if (clickCount.current >= 3) {
			clickCount.current = 0
			clearTimeout(clickTimer.current)
			setShowConfetti(true)
			clearTimeout(confettiTimer.current)
			confettiTimer.current = setTimeout(() => setShowConfetti(false), 700)
		} else {
			clearTimeout(clickTimer.current)
			clickTimer.current = setTimeout(() => {
				clickCount.current = 0
			}, 400)
		}
	}, [])

	return (
		<Tooltip content={getTooltip(count)}>
			<span
				className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full relative cursor-default${
					shrink ? ' flex-shrink-0' : ''
				}`}
				onClick={handleClick}
			>
				<StickyNoteIcon className="size-3" />
				{count}
				{showConfetti && <ConfettiParticles />}
			</span>
		</Tooltip>
	)
}
